import { CheckStroke, Search1Stroke } from '@lineiconshq/free-icons';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { useEffect, useRef, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import type { ContextMenuItem } from '@/components/custom/arito/context-menu/types';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface HeaderMenuProps {
  items: ContextMenuItem[];
  anchorEl: HTMLElement | null;
  open: boolean;
  searchValue?: string;
  onClose: () => void;
  onSearch?: (value: string) => void;
}

export function HeaderMenu({ items, anchorEl, open, searchValue = '', onClose, onSearch }: HeaderMenuProps) {
  const [draftSearchValue, setDraftSearchValue] = useState(searchValue);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hasSearchItem = items.some(item => item.type === 'search');

  useEffect(() => {
    if (!open || !anchorEl) {
      setAnchorRect(null);
      return;
    }

    const updateAnchorRect = () => {
      setAnchorRect(anchorEl.getBoundingClientRect());
    };

    updateAnchorRect();
    window.addEventListener('resize', updateAnchorRect);
    window.addEventListener('scroll', updateAnchorRect, true);

    return () => {
      window.removeEventListener('resize', updateAnchorRect);
      window.removeEventListener('scroll', updateAnchorRect, true);
    };
  }, [anchorEl, open]);

  useEffect(() => {
    setDraftSearchValue(searchValue);
  }, [searchValue]);

  useEffect(() => {
    if (!open) return;

    if (!hasSearchItem) return;

    const timeoutId = window.setTimeout(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    }, 30);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [hasSearchItem, open]);

  if (!anchorRect) {
    return null;
  }

  const commitSearch = () => {
    const nextSearchValue = draftSearchValue.trim();

    if (nextSearchValue !== searchValue.trim()) {
      onSearch?.(nextSearchValue);
    }
  };

  const handleSearchChange = (value: string) => {
    setDraftSearchValue(value);
  };

  return (
    <DropdownMenu open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
      <DropdownMenuTrigger asChild>
        <div
          aria-hidden='true'
          className='pointer-events-none fixed'
          style={{
            top: anchorRect.top,
            left: anchorRect.left,
            width: anchorRect.width,
            height: anchorRect.height
          }}
        />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side='bottom'
        align='start'
        sideOffset={4}
        className='z-[100000] w-[220px] rounded-md border border-slate-200 p-1 shadow-md'
        style={{ zIndex: 2147483647 }}
      >
        <DropdownMenuGroup>
          {items.map((item, index) => {
            if (item.type === 'divider') {
              return <DropdownMenuSeparator key={`divider-${index}`} className='my-1 bg-border' />;
            }

            if (item.type === 'search') {
              return (
                <div key={item.id} className='px-1 py-1.5'>
                  <div className='relative'>
                    <Lineicons
                      aria-hidden='true'
                      icon={Search1Stroke}
                      className='pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-sky-600'
                    />
                    <Input
                      ref={searchInputRef}
                      type='text'
                      value={draftSearchValue}
                      onChange={event => handleSearchChange(event.target.value)}
                      onBlur={commitSearch}
                      onClick={event => event.stopPropagation()}
                      onKeyDown={event => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          commitSearch();
                          onClose();
                        }

                        if (event.key === 'Escape') {
                          event.preventDefault();
                          onClose();
                        }
                      }}
                      placeholder={item.label}
                      className='h-8 rounded-sm border-slate-300 pl-8 pr-12 text-xs shadow-none focus-visible:ring-1 focus-visible:ring-sky-300'
                    />
                    {item.shortcut && (
                      <span className='pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500'>
                        {item.shortcut}
                      </span>
                    )}
                  </div>
                </div>
              );
            }

            return (
              <DropdownMenuItem
                key={item.id}
                onSelect={event => {
                  event.preventDefault();
                  item.onClick?.();
                  onClose();
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-sm border border-transparent px-2 py-1.5 text-left text-xs text-slate-700 outline-none transition-colors hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 focus-visible:border-sky-200 focus-visible:bg-sky-50 focus-visible:text-sky-700',
                  item.selected && 'font-medium text-slate-900'
                )}
              >
                {item.icon && <span className='shrink-0'>{item.icon}</span>}
                <span className='min-w-0 flex-1 truncate'>{item.label}</span>
                {item.shortcut && <DropdownMenuShortcut className='text-[10px]'>{item.shortcut}</DropdownMenuShortcut>}
                {item.selected && (
                  <Lineicons aria-hidden='true' icon={CheckStroke} className='size-3.5 shrink-0 text-sky-600' />
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
